import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { isSymplApp } from '@tokeet-frontend/tv3-platform'
import {
  ChannelNameTokens,
  Destroyable,
  selectApiChannelsWithDefaults,
  tokeetWebsitesChannel,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import * as moment from 'moment'
import { BehaviorSubject, merge } from 'rxjs'
import { distinctUntilChanged, map, startWith } from 'rxjs/operators'

@Component({
  selector: 'app-booking-fee-form',
  templateUrl: './booking-fee-form.component.html',
  styleUrls: ['./booking-fee-form.component.scss'],
})
export class BookingFeeFormComponent extends Destroyable implements OnInit {
  @Input() currencyCode: string
  @Input() form: FormGroup

  @Output() change = new EventEmitter()
  @Output() remove = new EventEmitter()

  minEndDate = new BehaviorSubject<Date>(null)
  maxStartDate = new BehaviorSubject<Date>(null)

  omitChannels = [
    ChannelNameTokens.AirBnBV2API,
    ChannelNameTokens.ExpediaAPI,
    ChannelNameTokens.AgodaAPI,
    ChannelNameTokens.BookingAPI,
    ChannelNameTokens.GoogleVacationRentals,
  ]
  channels$ = this.store.pipe(
    select(selectApiChannelsWithDefaults),
    map((items) => items.filter((t) => !this.omitChannels.includes(t.name))),
    map((items) => {
      if (isSymplApp()) {
        return items
          .filter((t) => t.name !== tokeetWebsitesChannel.name)
          .map((t) => ({ ...t, friendlyName: t.friendlyName.replace('AdvanceCM', 'Sympl') }))
      } else {
        return items
      }
    })
  )

  showDetails = false

  get startCtrl() {
    return this.form?.get('start') as FormControl
  }
  get endCtrl() {
    return this.form?.get('end') as FormControl
  }

  get amountPlaceholder() {
    return this.form.get('type').value === 'pct'
      ? $localize`Amount` + ' (%)'
      : $localize`Amount` + ' (' + this.currencyCode + ')'
  }

  feeCategories = [
    $localize`Administrative Fee`,
    $localize`Booking Fee`,
    $localize`Cleaning Fee`,
    $localize`Dock Fee`,
    $localize`Damage Deposit`,
    $localize`Guest Registration`,
    $localize`HOA Fee`,
    $localize`Hot Tub Fee`,
    $localize`Management Fee`,
    $localize`Parking Fee`,
    $localize`Pool Fee`,
    $localize`Pool Heating Fee`,
    $localize`Resort Fee`,
    $localize`Security Deposit Fee`,
    $localize`Service Fee`,
    $localize`Utility Fee`,
    $localize`Waiver Damage Fee`,
    $localize`Miscellaneous Fee`,
    $localize`Per Extra Person Fee`,
  ]

  constructor(private store: Store<any>) {
    super()
  }

  ngOnInit() {
    this.form.valueChanges.pipe(untilDestroy(this)).subscribe((values) => {
      this.change.emit(values)
    })

    this.form
      .get('type')
      .valueChanges.pipe(startWith(this.form.get('type').value), distinctUntilChanged(), untilDestroy(this))
      .subscribe((type) => {
        if (type === 'pct') {
          this.form.get('amount').setValidators([Validators.min(1), Validators.max(100)])
        } else {
          this.form.get('amount').setValidators([Validators.min(1)])
        }
        this.form.get('amount').updateValueAndValidity()
      })

    // TV4-5604: upfront and due_upon_arrival are mutually exclusive.
    // startWith ensures the disabled state is applied on init (not just on change).
    this.form
      .get('upfront')
      .valueChanges.pipe(startWith(this.form.get('upfront').value), distinctUntilChanged(), untilDestroy(this))
      .subscribe((upfront) => {
        const duaCtrl = this.form.get('due_upon_arrival')
        if (upfront) {
          duaCtrl.setValue(false, { emitEvent: false })
          duaCtrl.disable({ emitEvent: false })
        } else {
          duaCtrl.enable({ emitEvent: false })
        }
      })

    this.form
      .get('due_upon_arrival')
      .valueChanges.pipe(startWith(this.form.get('due_upon_arrival').value), distinctUntilChanged(), untilDestroy(this))
      .subscribe((dua) => {
        const upfrontCtrl = this.form.get('upfront')
        if (dua) {
          upfrontCtrl.setValue(false, { emitEvent: false })
          upfrontCtrl.disable({ emitEvent: false })
        } else {
          upfrontCtrl.enable({ emitEvent: false })
        }
      })

    merge(
      this.startCtrl.valueChanges.pipe(startWith(this.startCtrl.value), distinctUntilChanged()),
      this.endCtrl.valueChanges.pipe(startWith(this.endCtrl.value), distinctUntilChanged())
    )
      .pipe(untilDestroy(this))
      .subscribe(() => {
        this.setStartEndMinMax()
      })
  }

  endDateInPast() {
    return moment(this.endCtrl.value).isBefore(moment())
  }

  setStartEndMinMax() {
    if (this.startCtrl.value) {
      this.endCtrl.setValidators([Validators.required])
      const arrive = moment(this.startCtrl.value).add(1, 'days').startOf('day')
      if (`${this.minEndDate.value}` !== arrive.toDate().toString()) {
        // prevent date changed event when set min
        const today = moment()
        if (arrive.isAfter(today)) {
          this.minEndDate.next(arrive.toDate())
        } else {
          this.minEndDate.next(today.toDate())
        }
      }
    }

    if (this.endCtrl.value) {
      this.startCtrl.setValidators([Validators.required])
      const depart = moment(this.endCtrl.value).subtract(1, 'days').endOf('day')
      if (`${this.maxStartDate.value}` !== depart.toDate().toString()) {
        // prevent date changed event when set max
        this.maxStartDate.next(depart.toDate())
      }
    }
  }
}
